import React, { useEffect, useState } from 'react';
import { ReactFlow, ReactFlowProvider, Background, Controls, MiniMap } from '@xyflow/react';
import { TextField, Autocomplete, Button, Dialog, DialogTitle, DialogContent } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import ReplayIcon from '@mui/icons-material/Replay';
import NodeComponent from './NodeComponent';
import StaticEdgeComponent from './StaticEdgeComponent';
import axios from "axios";
import { APIURL } from "../../config";
import WorkflowBuilder from './WorkflowBuilder';
import { WF_STATUS } from './workflowConstants';

import '@xyflow/react/dist/style.css';

// No `onEditNode` and no `variant` → NodeComponent renders read-only view nodes
const nodeTypes = {
    'taskTypeNode': NodeComponent,
    'decisionNode': NodeComponent,
    'approvalNode': NodeComponent,
    'inputNode': NodeComponent,
    'outputNode': NodeComponent
};
const edgeTypes = { 'actionEdge': StaticEdgeComponent };

const nodeColor = (node) => {
    switch (node.type) {
        case 'inputNode':
            return 'lightgreen';
        case 'outputNode':
            return '#ff4d4d';
        case 'decisionNode':
            return 'pink';
        case 'approvalNode':
            return 'plum';
        default:
            return 'lightblue';
    }
};

const isJSON = (str) => {
    try {
        JSON.parse(str);
        return true;
    } catch (e) {
        return false;
    }
}

const ReactFlowComponent = (props) => {
    const [companyWorkflows, setCompanyWorkflows] = useState([]);
    const [selectedWorkflow, setSelectedWorkflow] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [confirmationOpen, setConfirmationOpen] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        await axios.get(APIURL + "/workflows", {
            params: { compID: props.comp_id },
        }).then((response) => {
            setCompanyWorkflows(response.data.map((wf) => {
                return {
                    ...wf,
                    optionGroup: wf.wf_status === WF_STATUS.PUBLISHED ? 'Published' : 'Draft'
                }
            }));
            setSelectedWorkflow(selectedWorkflow ? response.data.find((wf) => wf.wf_id === selectedWorkflow.wf_id) : null);
        }).catch((error) => {
            console.log(error);
        });
    };

    const confirmationDialog = () => {
        const handleConfirm = () => {
            setConfirmationOpen(false);
            handleWorkflowStatus(WF_STATUS.DRAFT)
        };

        const handleCancel = () => {
            setConfirmationOpen(false);
        };

        return (
            <div>
                <Dialog open={confirmationOpen} onClose={handleCancel}>
                    <DialogTitle>Confirm Action</DialogTitle>
                    <DialogContent>
                        <div>Unpublishing this workflow would stop it from affecting any active workflow instances. Are you sure you want to continue?</div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
                            <Button onClick={handleCancel}>Cancel</Button>
                            <Button onClick={handleConfirm} variant="contained" color="primary">Yes, Continue</Button>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>
        );
    };

    const handleWorkflowStatus = async (WfStatus) => {
        await axios
            .put(APIURL + `/workflows/status`, {
                wf_id: selectedWorkflow.wf_id,
                wf_status: WfStatus,
                wf_createdBy: props.user.emp_id,
            })
            .catch(function (error) {
                console.log(error);
            });

        setEditMode(false);
        fetchData();
    };

    return (
        <div style={{ width: '100%', height: 'calc(100vh - 185px)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10, height: 40 }}>
                <Autocomplete
                    id="wf-select"
                    options={[...companyWorkflows].sort((a, b) => -a.optionGroup.localeCompare(b.optionGroup))}
                    value={selectedWorkflow}
                    groupBy={(option) => option.optionGroup}
                    getOptionLabel={(option) => option.wf_title}
                    isOptionEqualToValue={(option, value) => option.wf_id === value.wf_id}
                    onChange={(event, value) => { setSelectedWorkflow(value); setEditMode(false); }}
                    renderOption={(props, option) => (
                        <li {...props} key={option.wf_id}>
                            {option.wf_title}
                        </li>
                    )}
                    renderInput={(params) => <TextField {...params} label="Select Workflow" />}
                    sx={{ width: 250 }}
                    size="small"
                />
                <div style={{ display: 'flex' }}>
                    {selectedWorkflow && !editMode &&
                        <Button
                            variant="contained"
                            size="small"
                            color={selectedWorkflow?.wf_status === WF_STATUS.PUBLISHED ? "error" : "success"}
                            startIcon={selectedWorkflow?.wf_status === WF_STATUS.PUBLISHED ? <ReplayIcon /> : <VerifiedIcon />}
                            onClick={() => { selectedWorkflow?.wf_status === WF_STATUS.PUBLISHED ? setConfirmationOpen(true) : handleWorkflowStatus(WF_STATUS.PUBLISHED) }}
                            sx={{ marginRight: 1 }}
                        >
                            {selectedWorkflow?.wf_status === WF_STATUS.PUBLISHED ? 'Unpublish Workflow' : 'Publish Workflow'}
                        </Button>
                    }
                    <Button
                        variant={editMode ? "outlined" : "contained"}
                        size="small"
                        color={editMode ? "error" : "primary"}
                        startIcon={editMode ? <CloseIcon /> : <EditIcon />}
                        onClick={() => setEditMode(!editMode)}
                        disabled={!selectedWorkflow}
                    >
                        {editMode ? 'Cancel Edit' : 'Edit Workflow'}
                    </Button>
                </div>
            </div>
            {selectedWorkflow && isJSON(selectedWorkflow.wf_nodes) && isJSON(selectedWorkflow.wf_edges) && !editMode &&
                <ReactFlow
                    nodes={JSON.parse(selectedWorkflow.wf_nodes)}
                    edges={JSON.parse(selectedWorkflow.wf_edges)}
                    nodeTypes={nodeTypes}
                    edgeTypes={edgeTypes}
                    fitView
                    nodesConnectable={false}
                    elementsSelectable={false}
                >
                    <Controls position="bottom-right" showInteractive={false} style={{ marginBottom: 175 }} />
                    <MiniMap nodeColor={nodeColor} zoomable pannable />
                    <Background variant="dots" gap={12} size={1} />
                </ReactFlow>
            }
            {editMode &&
                <WorkflowBuilder
                    {...props}
                    editMode={editMode}
                    wf_id={selectedWorkflow.wf_id}
                    wf_title={selectedWorkflow.wf_title}
                    wf_description={selectedWorkflow.wf_description}
                    wf_version={selectedWorkflow.wf_version}
                    wf_status={selectedWorkflow.wf_status}
                    nodes={JSON.parse(selectedWorkflow.wf_nodes)}
                    edges={JSON.parse(selectedWorkflow.wf_edges)}
                    fetchData={fetchData}
                    setEditMode={setEditMode}
                    setSelectedWorkflow={setSelectedWorkflow}
                />
            }
            {confirmationDialog()}
        </div>
    );
}

export default function WorkflowLibrary(props) {
    return (
        <ReactFlowProvider>
            <ReactFlowComponent {...props} />
        </ReactFlowProvider>
    );
}